import React, { useEffect, useState, useRef } from 'react';
import './SchedulePage.css';
import ScheduleCardCompact from "./ScheduleCardCompact";

interface ScheduleCategory {
  id: number;
  name: string;
  description: string;
  icon: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AvailableSchedule {
  id: number;
  name: string;
  category: string;
  description: string;
  icon: string;
  owner_type: string;
  is_selected: boolean;
  percentage: number;
  hours: number;
  target_hours: number;
  change_percentage: number;
  change_type: string;
  start_date?: string;
  end_date?: string;
}

interface SchedulePageProps {
  authenticatedFetch?: (url: string, options?: RequestInit) => Promise<Response>;
  onScheduleAdded: () => void;
  onBack: () => void;
}

const YEARS = [
  '2024',
  '2025',
  '2026',
  '2027',
  '2028',
  '2029',
  '2030',
  '2031',
  '2032',
  '2033',
  '2034',
];
const MENU = ['全部行程', ...YEARS, '自定义'];

const SchedulePage: React.FC<SchedulePageProps> = ({ 
  authenticatedFetch, 
  onScheduleAdded, 
  onBack 
}) => {
  const [schedules] = useState<AvailableSchedule[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('全部行程');
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<AvailableSchedule | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isOngoing, setIsOngoing] = useState(true);
  const [message, setMessage] = useState('');
  // 新增行程弹窗相关state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newItineraryName, setNewItineraryName] = useState('');
  const [newItineraryTopic, setNewItineraryTopic] = useState('');
  const [newItineraryYear, setNewItineraryYear] = useState(YEARS[0]);
  const [localItineraries, setLocalItineraries] = useState<any[]>([]);
  const [editingItinerary, setEditingItinerary] = useState<any | null>(null);
  const [slidingId, setSlidingId] = useState<number | null>(null);
  const [slidingX, setSlidingX] = useState<number>(0);
  const [swipedId, setSwipedId] = useState<number | null>(null);
  const dragStartX = useRef<number>(0);
  const MAX_SWIPE = -80;
  const DAMPING = 0.3;

  useEffect(() => {
    // 设置默认开始时间为当前时间（东八区）
    const now = new Date();
    // 获取东八区时间（UTC+8）
    const utc8Time = new Date(now.getTime() + (8 * 60 * 60 * 1000));
    setStartDate(utc8Time.toISOString().slice(0, 16));
  }, []);

  // 滑动事件
  const handleTouchStart = (id: number, e: React.TouchEvent) => {
    if (swipedId !== null && swipedId !== id) setSwipedId(null);
    setSlidingId(id);
    setSlidingX(0);
    dragStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (id: number, e: React.TouchEvent) => {
    if (slidingId !== id) return;
    let deltaX = e.touches[0].clientX - dragStartX.current;
    if (deltaX < MAX_SWIPE) deltaX = MAX_SWIPE + (deltaX - MAX_SWIPE) * DAMPING;
    setSlidingX(Math.min(0, deltaX));
  };
  const handleTouchEnd = (id: number) => {
    if (slidingId !== id) return;
    if (slidingX < MAX_SWIPE / 2) {
      setSwipedId(id);
      setSlidingX(MAX_SWIPE);
    } else {
      setSwipedId(null);
      setSlidingX(0);
    }
    setSlidingId(null);
  };
  // 鼠标事件
  const handleMouseDown = (id: number, e: React.MouseEvent) => {
    if (swipedId !== null && swipedId !== id) setSwipedId(null);
    setSlidingId(id);
    setSlidingX(0);
    dragStartX.current = e.clientX;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };
  const onMouseMove = (e: MouseEvent) => {
    if (slidingId === null) return;
    let deltaX = e.clientX - dragStartX.current;
    if (deltaX < MAX_SWIPE) deltaX = MAX_SWIPE + (deltaX - MAX_SWIPE) * DAMPING;
    setSlidingX(Math.min(0, deltaX));
  };
  const onMouseUp = () => {
    if (slidingId === null) return;
    if (slidingX < MAX_SWIPE / 2) {
      setSwipedId(slidingId);
      setSlidingX(MAX_SWIPE);
    } else {
      setSwipedId(null);
      setSlidingX(0);
    }
    setSlidingId(null);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  // 根据选中的分类过滤行程，并排除已选中的行程
  const getFilteredSchedules = () => {
    // 首先过滤掉已选中的行程
    const unselectedSchedules = schedules.filter(schedule => !schedule.is_selected);
    
    if (selectedCategory === '全部行程') {
      return unselectedSchedules.filter(schedule => schedule.owner_type === 'system');
    } else if (YEARS.includes(selectedCategory)) {
      // 这里可以根据年份进一步过滤（如有需要）
      return unselectedSchedules.filter(schedule => schedule.owner_type === 'system');
    } else if (selectedCategory === '自定义') {
      return unselectedSchedules.filter(schedule => schedule.owner_type === 'user');
    } else {
      return [];
    }
  };

  const filteredSchedules = getFilteredSchedules();

  // 处理行程点击
  const handleScheduleClick = (schedule: AvailableSchedule) => {
    setSelectedSchedule(schedule);
    setShowTimeForm(true);
  };

  // 添加行程（已删除后端接口调用）
  const handleAddSchedule = () => {
    if (!selectedSchedule) return;
    
    setMessage('行程添加功能已禁用');
    setShowTimeForm(false);
    setSelectedSchedule(null);
    // 延迟返回首页
    setTimeout(() => {
      onBack();
    }, 1500);
  };

  // 新增行程按钮点击
  const handleAddItinerary = () => {
    if (editingItinerary) return; // 只允许一个可编辑卡片
    const nowYear = new Date().getFullYear().toString();
    setEditingItinerary({
      id: Date.now(),
      name: '新的行程',
      year: selectedCategory === '全部行程' ? nowYear : (selectedCategory === '自定义' ? YEARS[0] : selectedCategory)
    });
  };
  // 保存编辑
  const handleSaveEdit = () => {
    if (!editingItinerary.name) return;
    setLocalItineraries(prev => [editingItinerary, ...prev]);
    setEditingItinerary(null);
  };
  // 取消编辑
  const handleCancelEdit = () => {
    setEditingItinerary(null);
  };
  // 删除行程
  const handleDeleteItinerary = (id: number) => {
    setLocalItineraries(prev => prev.filter(item => item.id !== id));
  };

  if (showTimeForm) {
    return (
      <div className="add-schedule-page">
        <div className="add-schedule-header">
          <h2>设置行程时间</h2>
        </div>
        
        <div className="time-form-container">
          <div className="selected-schedule-info">
            <div className="schedule-icon">{selectedSchedule?.icon}</div>
            <div className="schedule-info">
              <h3>{selectedSchedule?.name}</h3>
              <p>{selectedSchedule?.description}</p>
            </div>
          </div>
          
          <div className="form-group">
            <label>开始时间：</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={isOngoing}
                onChange={(e) => setIsOngoing(e.target.checked)}
              />
              一直持续
            </label>
          </div>
          
          {!isOngoing && (
            <div className="form-group">
              <label>结束时间：</label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>
          )}
          
          <div className="form-actions">
            <button onClick={handleAddSchedule} className="add-btn">
              添加行程
            </button>
            <button onClick={() => setShowTimeForm(false)} className="cancel-btn">
              取消
            </button>
          </div>
        </div>
        
        {message && <div className="message">{message}</div>}
      </div>
    );
  }

  return (
    <>
      <div className="add-schedule-layout">
        <aside className="sidebar">
          <div className="category-list">
            {MENU.map(menu => (
              <div
                key={menu}
                className={`category-item${selectedCategory === menu ? ' active' : ''}`}
                onClick={() => setSelectedCategory(menu)}
              >
                {menu}
              </div>
            ))}
          </div>
        </aside>
        <main className="schedule-main">
          <div className="itinerary-grid">
            {/* 本地新增的行程卡片展示 */}
            {localItineraries
              .filter(item => selectedCategory === '全部行程' ? true : (item.year === selectedCategory))
              .map(item => {
                const isSliding = slidingId === item.id;
                const isSwiped = swipedId === item.id;
                return (
                  <div key={item.id} className="itinerary-card-swipe-wrapper" style={{position:'relative',overflow:'hidden'}}>
                    {/* 删除按钮 */}
                    <button
                      className="swipe-delete-btn"
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        height: '100%',
                        width: 80,
                        background: '#ff4d4f',
                        color: '#fff',
                        border: 'none',
                        fontWeight: 500,
                        fontSize: '16px',
                        cursor: 'pointer',
                        zIndex: 1,
                        transition: 'background 0.2s',
                        pointerEvents: isSwiped ? 'auto' : 'none',
                        opacity: isSwiped ? 1 : 0.7
                      }}
                      onClick={() => handleDeleteItinerary(item.id)}
                    >删除</button>
                    {/* 卡片内容 */}
                    <div
                      className="itinerary-card"
                      style={{
                        position: 'relative',
                        width: '100%',
                        transform: `translateX(${isSliding ? slidingX : isSwiped ? MAX_SWIPE : 0}px)`,
                        transition: isSliding ? 'none' : 'transform 0.2s',
                        background: '#fff',
                        zIndex: 2
                      }}
                      onTouchStart={e => handleTouchStart(item.id, e)}
                      onTouchMove={e => handleTouchMove(item.id, e)}
                      onTouchEnd={() => handleTouchEnd(item.id)}
                      onMouseDown={e => handleMouseDown(item.id, e)}
                    >
                      <div className="itinerary-name">{item.name}</div>
                    </div>
                  </div>
                );
              })}
            {/* 编辑态卡片，只允许一个 */}
            {editingItinerary &&
              (selectedCategory === '全部行程' || editingItinerary.year === selectedCategory) && (
                <div className="itinerary-card editing">
                  <input
                    className="modal-input"
                    placeholder="行程名称"
                    value={editingItinerary.name}
                    autoFocus
                    onChange={e => setEditingItinerary({ ...editingItinerary, name: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); }}
                  />
                  <div className="modal-actions">
                    <button className="modal-btn" onClick={handleSaveEdit}>保存</button>
                    <button className="modal-btn cancel" onClick={handleCancelEdit}>取消</button>
                  </div>
                </div>
              )}
            {/* 新增行程按钮，始终在最后 */}
            {!editingItinerary && (
              <button className="add-itinerary-btn" onClick={handleAddItinerary}>+</button>
            )}
            {filteredSchedules.map(schedule => (
              <ScheduleCardCompact
                key={schedule.id}
                icon={schedule.icon}
                name={schedule.name}
                onClick={() => handleScheduleClick(schedule)}
              />
            ))}
          </div>
        </main>
      </div>
    </>
  );
};

export default SchedulePage; 